/**
 * Generic Link Context Menu Utility
 * 
 * A customizable, framework-agnostic utility that adds custom right-click context menus
 * to links on any web page. Supports link exclusion rules and extensive customization.
 * 
 * @author Devin AI
 * @version 1.0.0
 */

class LinkContextMenu {
  constructor(options = {}) {
    // Default actions that are always present
    const defaultActions = [
      { text: 'Open in Evo Tab', action: 'openInNewTab' },
      { text: 'Open in Browser Tab', action: 'handleLink' }
    ];
    
    // Combine default actions with any extra actions provided by user
    const allMenuOptions = [
      ...defaultActions,
      ...(options.extraActions || [])
    ];

    this.config = {
      enableNativeTabOnly: options.enableNativeTabOnly !== false, // Default: true
      linkSelector: options.linkSelector || 'a[href]',
      
      menuId: options.menuId || 'generic-link-context-menu',
      menuText: options.menuText || 'Handle Link',
      menuOptions: allMenuOptions,
      
      menuStyles: {
        position: 'absolute',
        background: '#ffffff',
        border: '1px solid #d0d0d0',
        borderRadius: '4px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
        padding: '4px 0',
        display: 'none',
        zIndex: '10000',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: '13px',
        minWidth: '160px',
        userSelect: 'none',
        ...options.menuStyles
      },
      
      optionStyles: {
        padding: '8px 16px',
        cursor: 'pointer',
        color: '#333333',
        transition: 'background-color 0.1s ease',
        ...options.optionStyles
      },
      
      hoverStyles: {
        backgroundColor: '#e8f0fe',
        ...options.hoverStyles
      },

      hintTextStyles: {
        padding: '8px 16px',
        color: '#666666',
        fontSize: '11px',
        fontStyle: 'italic',
        borderTop: '1px solid #e0e0e0',
        cursor: 'default',
        ...options.hintTextStyles
      },
      
      onLinkClick: options.onLinkClick || this.defaultHandleLink,
      onOpenInNewTab: options.onOpenInNewTab || this.defaultOpenInNewTab,
      onMenuShow: options.onMenuShow || null,
      onMenuHide: options.onMenuHide || null,
      instanceId: options.instanceId || null,
      widgetId: options.widgetId ,
      userContext: options.userContext || null,
      platformUrl: options.platformUrl || null,
      
      excludeSelectors: options.excludeSelectors || [],
      excludeUrls: options.excludeUrls || [],
      excludePatterns: options.excludePatterns || [],
      excludeClasses: options.excludeClasses || [],
      excludeAttributes: options.excludeAttributes || {},
      
      debug: options.debug || false,
      preventDefaultBehavior: options.preventDefaultBehavior !== false, // Default: true
      hideOnScroll: options.hideOnScroll !== false, // Default: true
      hideOnResize: options.hideOnResize !== false, // Default: true
      hideOnEscape: options.hideOnEscape !== false, // Default: true
      doubleClickThreshold: options.doubleClickThreshold || 400, // ms
      
      // Copy all custom action handlers from options
      ...Object.fromEntries(
        Object.entries(options).filter(([key]) => key.startsWith('on') && key !== 'onLinkClick' && key !== 'onOpenInNewTab' && key !== 'onMenuShow' && key !== 'onMenuHide')
      )
    };
    
    this.menu = null;
    this.menuOptions = [];
    this.currentLinkHref = null;
    this.currentLinkElement = null;
    this.isInitialized = false;
    this.lastRightClickTime = 0;
    
    this.log('LinkContextMenu initialized with config:', this.config);
  }
  
  defaultHandleLink = (url, linkElement, event) => {
    // Default behavior: open in new browser tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  
  defaultOpenInNewTab = (url, linkElement, event) => {
    this.openWorkspaceTab(url, linkElement);
  }
  
  openWorkspaceTab(url, linkElement) {
    let instanceId = this.config.instanceId;
    if (!instanceId && this.config.userContext) {
      instanceId = this.config.userContext.instanceId;
    }
    
    if (!instanceId) {
      console.warn('LinkContextMenu: Cannot open workspace tab - instanceId not provided');
      return;
    }
    
    let fullUrl;
    try {
      // If url is already absolute, use it as-is
      if (url.startsWith('http://') || url.startsWith('https://')) {
        fullUrl = url;
      } else {
        // If url is relative, construct absolute URL
        fullUrl = new URL(url, window.location.origin).href;
      }
    } catch (error) {
      console.warn('LinkContextMenu: Failed to parse URL, constructing fallback:', error);
      // Fallback: if it starts with /, prepend origin; otherwise use as-is
      if (url.startsWith('/')) {
        fullUrl = window.location.origin + url;
      } else {
        fullUrl = url;
      }
    }
    
    const message = {
      type: 'LaunchProduct',
      widgetId: this.config.widgetId,
      instanceId: instanceId,
      url: fullUrl, // Pass the complete URL instead of just pathname
      name: linkElement.textContent.trim() || 'New Tab',
      behaviour: 2 // ForceNew - always open in new tab
    };
    
    console.log('LinkContextMenu: Opening workspace tab via LaunchProduct:', message);
    window.parent.postMessage(JSON.stringify(message), this.config.platformUrl);
  }
  
  log(...args) {
    if (this.config.debug) {
      console.log('[LinkContextMenu]', ...args);
    }
  }
  
  
  isNativeTab() {
    return window.location !== window.parent.location;
  }
  
  shouldExcludeLink(linkElement) {
    for (const selector of this.config.excludeSelectors) {
      if (linkElement.matches(selector)) {
        this.log('Link excluded by selector:', selector);
        return true;
      }
    }
    
    const href = linkElement.href;
    for (const url of this.config.excludeUrls) {
      if (href === url || href.includes(url)) {
        this.log('Link excluded by URL:', url);
        return true;
      }
    }
    
    for (const pattern of this.config.excludePatterns) {
      const regex = new RegExp(pattern);
      if (regex.test(href)) {
        this.log('Link excluded by pattern:', pattern);
        return true;
      }
    }
    
    for (const className of this.config.excludeClasses) {
      if (linkElement.classList.contains(className)) {
        this.log('Link excluded by class:', className);
        return true;
      }
    }
    
    for (const [attr, value] of Object.entries(this.config.excludeAttributes)) {
      const attrValue = linkElement.getAttribute(attr);
      if (value === true && attrValue !== null) {
        this.log('Link excluded by attribute presence:', attr);
        return true;
      } else if (attrValue === value) {
        this.log('Link excluded by attribute value:', attr, '=', value);
        return true;
      }
    }
    
    return false;
  }
  
  applyStyles(element, styles) {
    Object.assign(element.style, styles);
  }
  
  createMenu() {
    this.menu = document.createElement('div');
    this.menu.id = this.config.menuId;
    this.applyStyles(this.menu, this.config.menuStyles);
    
    this.menuOptions = [];
    
    this.config.menuOptions.forEach((option, index) => {
      const menuOption = document.createElement('div');
      menuOption.textContent = option.text;
      menuOption.dataset.action = option.action;
      this.applyStyles(menuOption, this.config.optionStyles);
      
      menuOption.addEventListener('mouseenter', () => {
        this.applyStyles(menuOption, this.config.hoverStyles);
      });
      
      menuOption.addEventListener('mouseleave', () => {
        this.applyStyles(menuOption, {
          ...this.config.optionStyles,
          backgroundColor: '#ffffff' // Explicitly reset to white background
        });
      });
      
      menuOption.addEventListener('click', (event) => this.handleMenuOptionClick.call(this, event, option.action));
      
      this.menu.appendChild(menuOption);
      this.menuOptions.push(menuOption);
    });

    const hintText = document.createElement('div');
    hintText.textContent = 'Right-click again for more options';
    this.applyStyles(hintText, this.config.hintTextStyles);
    this.menu.appendChild(hintText);
    
    document.body.appendChild(this.menu);
    this.log('Menu created with', this.config.menuOptions.length, 'options');
  }
  
  positionMenu(x, y) {
    if (!this.menu) {
      this.log('Cannot position menu - menu element not created');
      return;
    }
    
    this.menu.style.left = `${x}px`;
    this.menu.style.top = `${y}px`;
    this.menu.style.display = 'block';
    
    const rect = this.menu.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    if (rect.right > viewportWidth) {
      this.menu.style.left = `${x - rect.width}px`;
    }
    if (rect.bottom > viewportHeight) {
      this.menu.style.top = `${y - rect.height}px`;
    }
    
    this.log('Menu positioned at:', x, y);
  }
  
  showMenu(x, y, linkElement) {
    if (!this.menu) {
      this.log('Cannot show menu - menu element not created');
      return;
    }
    
    this.currentLinkHref = linkElement.href;
    this.currentLinkElement = linkElement;
    
    this.positionMenu(x, y);
    
    if (this.config.onMenuShow) {
      this.config.onMenuShow(linkElement, this.currentLinkHref);
    }
    
    this.log('Menu shown for link:', this.currentLinkHref);
  }
  
  hideMenu() {
    if (this.menu) {
      this.menu.style.display = 'none';
      
      this.menuOptions.forEach(menuOption => {
        this.applyStyles(menuOption, this.config.optionStyles);
      });
      
      if (this.config.onMenuHide) {
        this.config.onMenuHide(this.currentLinkElement, this.currentLinkHref);
      }
      
      this.currentLinkHref = null;
      this.currentLinkElement = null;
      
      this.log('Menu hidden');
    }
  }
  
  handleContextMenu(e) {
    const link = e.target.closest(this.config.linkSelector);
    
    if (link && link.href && !this.shouldExcludeLink(link)) {
      const now = Date.now();
      const timeSinceLast = now - this.lastRightClickTime;
      this.lastRightClickTime = now;
      
      if (timeSinceLast < this.config.doubleClickThreshold) {
        this.log('Double right-click detected, showing native menu for:', link.href);
        this.hideMenu();
        return; // Don't preventDefault, let native menu appear
      }
      
      if (this.config.preventDefaultBehavior) {
        e.preventDefault();
      }
      
      const linkText = link.textContent.trim();
      this.log('Right-clicked on link:', link.href, 'Text:', linkText);
      
      this.showMenu(e.pageX, e.pageY, link);
    } else {
      this.hideMenu();
    }
  }
  
  handleMenuOptionClick(event, action) {
    if (!this.currentLinkHref || !this.currentLinkElement) return;
    
    switch (action) {
      case 'handleLink':
        this.config.onLinkClick(this.currentLinkHref, this.currentLinkElement, event);
        break;
      case 'openInNewTab':
        this.config.onOpenInNewTab(this.currentLinkHref, this.currentLinkElement, event);
        break;
      default:
        // Check if user provided a custom handler for this action
        const customHandler = this.config[`on${action.charAt(0).toUpperCase() + action.slice(1)}`];
        if (customHandler && typeof customHandler === 'function') {
          customHandler(this.currentLinkHref, this.currentLinkElement, event);
        } else {
          console.warn('LinkContextMenu: Unknown action:', action);
        }
    }
    this.hideMenu();
  }
  
  setupEventListeners() {
    document.addEventListener('contextmenu', (e) => this.handleContextMenu(e));
    
    document.addEventListener('click', (e) => {
      if (this.menu && !this.menu.contains(e.target)) {
        this.hideMenu();
      }
    });
    
    if (this.config.hideOnScroll) {
      document.addEventListener('scroll', () => this.hideMenu());
    }
    
    if (this.config.hideOnResize) {
      window.addEventListener('resize', () => this.hideMenu());
    }
    
    if (this.config.hideOnEscape) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.hideMenu();
        }
      });
    }
    
    this.log('Event listeners set up');
  }
  
  initialize() {
    if (this.isInitialized) {
      this.log('Already initialized, skipping');
      return this;
    }
    
    if (this.config.enableNativeTabOnly && !this.isNativeTab()) {
      this.log('Link context menu disabled - not running in native tab');
      return this;
    }
    
    this.log('Initializing link context menu');
    
    this.createMenu();
    this.setupEventListeners();
    this.isInitialized = true;
    
    this.log('Link context menu initialized successfully');
    return this;
  }
  
  destroy() {
    if (this.menu && this.menu.parentNode) {
      this.menu.parentNode.removeChild(this.menu);
    }
    
    this.menu = null;
    this.menuOptions = [];
    this.currentLinkHref = null;
    this.currentLinkElement = null;
    this.isInitialized = false;
    
    this.log('Link context menu destroyed');
  }
  
  updateConfig(newOptions) {
    this.config = { ...this.config, ...newOptions };
    this.log('Configuration updated:', newOptions);
    
    if (this.isInitialized) {
      this.destroy();
      this.initialize();
    }
    
    return this;
  }
}

function createLinkContextMenu(options = {}) {
  return new LinkContextMenu(options);
}

let defaultInstance = null;

function initializeLinkContextMenu(options = {}) {
  if (defaultInstance) {
    defaultInstance.destroy();
  }
  
  defaultInstance = new LinkContextMenu(options);
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => defaultInstance.initialize());
  } else {
    defaultInstance.initialize();
  }
  
  return defaultInstance;
}

function handleLink(url) {
  console.log("handleLink called with URL:", url);
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { LinkContextMenu, createLinkContextMenu, initializeLinkContextMenu, handleLink };
} else {
  window.LinkContextMenu = LinkContextMenu;
  window.createLinkContextMenu = createLinkContextMenu;
  window.initializeLinkContextMenu = initializeLinkContextMenu;
  window.handleLink = handleLink;
}

export { LinkContextMenu, createLinkContextMenu, initializeLinkContextMenu, handleLink };
